import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ShieldCheck, Wifi, WifiOff, AlertTriangle, CheckCircle, MapPin, Battery } from 'lucide-react'
import api from '../../services/api'
import { requestAndGetLocation } from '../../utils/geo'

const NAVY = '#1B2B4B'
const SOS_HOLD_DURATION = 3000

export default function LoneWorkerActive() {
  const navigate = useNavigate()
  const location = useLocation()
  const base = location.pathname.startsWith('/m/') ? '/m/lone-worker' : '/lone-worker'
  const [session, setSession] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [checkingIn, setCheckingIn] = useState(false)
  const [checkinSuccess, setCheckinSuccess] = useState(false)
  const [secondsUntil, setSecondsUntil] = useState(0)
  const [totalSeconds, setTotalSeconds] = useState(0)
  const [sosProgress, setSosProgress] = useState(0)
  const [sosHolding, setSosHolding] = useState(false)
  const [showOfflineModal, setShowOfflineModal] = useState(false)
  const sosTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sosIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/lone-worker/active')
      if (!data) { navigate(base, { replace: true }); return }
      setSession(data)
      if (!loading) setLoading(false)
    } catch {}
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [])

  // Countdown tick
  useEffect(() => {
    if (!session?.next_checkin_due) return
    const update = () => {
      const dueMs = new Date(session.next_checkin_due).getTime()
      const nowMs = Date.now()
      const secs = Math.max(0, Math.round((dueMs - nowMs) / 1000))
      setSecondsUntil(secs)
      setTotalSeconds(session.checkin_interval_minutes * 60)
    }
    update()
    tickRef.current = setInterval(update, 1000)
    return () => { if (tickRef.current) clearInterval(tickRef.current) }
  }, [session?.next_checkin_due])

  const handleCheckin = async () => {
    if (!session || checkingIn) return
    const wasSos = session.status === 'sos'
    setCheckingIn(true)
    try {
      let latitude, longitude
      try {
        const pos = await requestAndGetLocation()
        latitude = pos.lat
        longitude = pos.lng
      } catch {}

      await api.post(`/lone-worker/${session.id}/checkin`, { latitude, longitude })
      setCheckinSuccess(true)
      setTimeout(() => setCheckinSuccess(false), wasSos ? 4000 : 3000)
      await load()
    } catch {}
    setCheckingIn(false)
  }

  const handleSosDown = () => {
    setSosHolding(true)
    setSosProgress(0)
    const start = Date.now()
    sosIntervalRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - start) / SOS_HOLD_DURATION) * 100)
      setSosProgress(pct)
    }, 50)
    sosTimerRef.current = setTimeout(async () => {
      setSosHolding(false); setSosProgress(0)
      try {
        let lat, lng
        try {
          const pos = await requestAndGetLocation()
          lat = pos.lat; lng = pos.lng
        } catch {}
        await api.post(`/lone-worker/${session.id}/sos`, { latitude: lat, longitude: lng })
        await load()
      } catch {}
    }, SOS_HOLD_DURATION)
  }

  const handleSosUp = () => {
    setSosHolding(false); setSosProgress(0)
    if (sosTimerRef.current) clearTimeout(sosTimerRef.current)
    if (sosIntervalRef.current) clearInterval(sosIntervalRef.current)
  }

  const handleComplete = async () => {
    if (!session) return
    if (!confirm('End your lone worker session?')) return
    await api.post(`/lone-worker/${session.id}/complete`).catch(() => {})
    navigate(base)
  }

  const toggleOfflineZone = async (entering: boolean) => {
    await api.post(`/lone-worker/${session.id}/offline-zone`, { entering }).catch(() => {})
    setShowOfflineModal(false)
    await load()
  }

  if (loading) return (
    <div className="flex items-center justify-center" style={{ background: NAVY, minHeight: base === '/m/lone-worker' ? '60vh' : '100vh' }}>
      <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (!session) return null

  const isSos = session.status === 'sos'
  const isOverdue = secondsUntil === 0 && session.next_checkin_due
  const pct = totalSeconds > 0 ? ((totalSeconds - secondsUntil) / totalSeconds) * 100 : 0
  const circumference = 2 * Math.PI * 110
  const strokeDash = `${(pct / 100) * circumference} ${circumference}`

  return (
    <div className="min-h-screen flex flex-col" style={{ background: isSos ? '#7B0000' : NAVY }}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          {session.offline_zone ? <WifiOff size={16} color="rgba(255,255,255,0.7)" /> : <Wifi size={16} color="rgba(255,255,255,0.7)" />}
          <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {session.offline_zone ? 'Offline Zone' : 'Connected'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: isSos ? '#ff4444' : '#3DB54A' }} />
          <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {isSos ? 'SOS ACTIVE' : 'Monitoring Active'}
          </span>
        </div>
      </div>

      {/* Activity label */}
      <div className="text-center px-5 mb-2">
        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {session.activity_type?.replace(/_/g, ' ')}
        </p>
        {session.location_description && (
          <p className="flex items-center justify-center gap-1 text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
            <MapPin size={12} />{session.location_description}
          </p>
        )}
      </div>

      {/* Giant check-in ring */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-4">
        <div className="relative" style={{ width: 260, height: 260 }}>
          <svg width="260" height="260" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="130" cy="130" r="110" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
            <circle cx="130" cy="130" r="110" fill="none"
              stroke={isOverdue ? '#F5921E' : '#3DB54A'}
              strokeWidth="8" strokeLinecap="round"
              strokeDasharray={strokeDash}
              style={{ transition: 'stroke-dasharray 1s linear' }}
            />
          </svg>

          {/* Center button */}
          <button
            onClick={handleCheckin}
            disabled={checkingIn}
            className="absolute inset-0 flex flex-col items-center justify-center rounded-full transition-all active:scale-95"
            style={{ margin: 20, background: checkinSuccess ? '#3DB54A' : isSos ? '#3DB54A' : isOverdue ? '#F5921E' : 'rgba(255,255,255,0.12)' }}
          >
            {checkinSuccess ? (
              <>
                <CheckCircle size={40} color="white" />
                <span className="text-sm font-bold text-white mt-2">SOS Cleared!</span>
              </>
            ) : checkingIn ? (
              <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
            ) : isSos ? (
              <>
                <CheckCircle size={40} color="white" />
                <span className="text-2xl font-extrabold text-white mt-2">I'M OK</span>
                <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>TAP TO CANCEL SOS</span>
              </>
            ) : (
              <>
                <ShieldCheck size={40} color="white" />
                <span className="text-2xl font-extrabold text-white mt-2">I'M OK</span>
                <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {isOverdue ? 'OVERDUE — tap now' : secondsUntil > 0 ? `${Math.floor(secondsUntil / 60)}m ${secondsUntil % 60}s` : ''}
                </span>
              </>
            )}
          </button>
        </div>

        {isOverdue && (
          <div className="mt-3 px-4 py-2 rounded-full" style={{ background: '#F5921E' }}>
            <span className="text-sm font-bold text-white">Check-in overdue — supervisors will be notified soon</span>
          </div>
        )}

        {/* SOS button */}
        <div className="mt-6 w-full max-w-xs">
          <button
            onMouseDown={handleSosDown}
            onMouseUp={handleSosUp}
            onTouchStart={handleSosDown}
            onTouchEnd={handleSosUp}
            className="w-full py-4 rounded-2xl font-extrabold text-lg relative overflow-hidden transition-transform active:scale-95"
            style={{ background: '#D93025', color: 'white', border: '3px solid rgba(255,255,255,0.3)' }}
          >
            {sosHolding && (
              <div
                className="absolute inset-0 transition-all"
                style={{ width: `${sosProgress}%`, background: 'rgba(255,255,255,0.25)' }}
              />
            )}
            <span className="relative flex items-center justify-center gap-2">
              <AlertTriangle size={22} />
              {sosHolding ? `Hold ${Math.round((sosProgress / 100) * 3)}s…` : 'SOS — Hold 3 sec'}
            </span>
          </button>
          <p className="text-xs text-center mt-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Hold for 3 seconds to trigger emergency alert
          </p>
        </div>
      </div>

      {/* Bottom actions — extra padding when inside MobileLayout (nav bar is 64px) */}
      <div className="px-5 space-y-2" style={{ paddingBottom: base === '/m/lone-worker' ? 'calc(80px + env(safe-area-inset-bottom))' : '2rem' }}>
        <button
          onClick={() => setShowOfflineModal(true)}
          className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.2)' }}
        >
          {session.offline_zone ? <><Wifi size={16} />Exit Offline Zone</> : <><WifiOff size={16} />Enter Offline Zone</>}
        </button>
        <button
          onClick={handleComplete}
          className="w-full py-3 rounded-xl font-semibold text-sm"
          style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: 'none' }}
        >
          End Session
        </button>
      </div>

      {/* Offline zone modal */}
      {showOfflineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h2 className="font-bold text-base mb-2" style={{ color: 'var(--text)' }}>
              {session.offline_zone ? 'Exit Offline Zone?' : 'Enter Offline Zone?'}
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {session.offline_zone
                ? 'Resume normal check-in monitoring.'
                : 'Check-in requirements will be suspended while you are in a no-signal area. A dead-man switch will activate when you return.'}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowOfflineModal(false)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'var(--bg)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>Cancel</button>
              <button onClick={() => toggleOfflineZone(!session.offline_zone)} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: NAVY }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
